import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import '../../components/v2/tokens.css';
import { AdvancedScoringSection } from '../../components/v2/organisms/AdvancedScoringSection';
import { AppSidebar } from '../../components/v2/organisms/AppSidebar';
import { MoodTermsCard } from '../../components/v2/organisms/MoodTermsCard';
import { PrimaryParametersCard } from '../../components/v2/organisms/PrimaryParametersCard';
import { ResultsPreviewGrid } from '../../components/v2/organisms/ResultsPreviewGrid';
import { SearchModeTabs } from '../../components/v2/organisms/SearchModeTabs';
import { TopNav } from '../../components/v2/organisms/TopNav';
import chrome from '../../components/v2/V2Chrome.module.css';
import type { SearchMode } from '../../components/v2/searchMode';
import { getCategories } from '../../helpers/getCategories';
import { Modes, Types, type Mood } from '../../helpers/types';
import { getUsername } from '../../helpers/users';
import { useSearchLogic, type RunSearchOptions } from '../../hooks/useSearchLogic';
import { useSearchState } from '../../hooks/useSearchState';
import { useUserData } from '../../hooks/useUserData';
import { useVideoFiltering } from '../../hooks/useVideoFiltering';
import { createShortLink } from '../../helpers/supabase/shortLinks';

import styles from './Search.module.css';

const SEARCH_PAGE_CHUNK = 100;
/** Replay anchor encoded in share links (`totalListingPages` counts from this listing page onward). */
const LISTING_SHARE_REPLAY_START = 1;

const modes: Modes = {
  newest: 'Newest videos',
  user: 'User ID',
  friend: 'Friend',
  category: 'Category',
  tags: 'Tags',
  extreme: 'Extreme',
  friendsEvents: "What's New",
};

const types: Types = {
  newest: [
    { value: 'newest', label: 'Straight' },
    { value: 'new-private', label: 'Straight (Private)' },
    { value: 'gay-newest', label: 'Gay' },
    { value: 'gay-private', label: 'Gay (Private)' },
  ],
  category: [
    { value: 'most-popular', label: 'Popular' },
    { value: 'latest', label: 'Newest' },
  ],
  tags: [
    { value: 'popular', label: 'Popular' },
    { value: 'latest', label: 'Newest' },
  ],
  user: [
    { value: 'public', label: 'Public' },
    { value: 'private', label: 'Private' },
    { value: 'favourite', label: 'Favorite' },
  ],
  friend: [
    { value: 'public', label: 'Public' },
    { value: 'private', label: 'Private' },
    { value: 'favourite', label: 'Favorite' },
  ],
  extreme: [
    { value: 'female-extreme', label: 'Straight' },
    { value: 'male-extreme', label: 'Gay' },
  ],
};

const SELECT_MOOD_PLACEHOLDER: Mood = {
  name: 'Select a mood',
  preferences: {
    tags: [],
    excludeTags: [],
    boosterTags: [],
    diminishingTags: [],
    minDuration: 0,
  },
};

const Search = () => {
  const searchState = useSearchState({ defaultMode: 'user' });
  const userData = useUserData();

  const videoFiltering = useVideoFiltering({
    params: searchState.params,
    searchObject: searchState.searchObject,
    syncedDefaultMood: userData.defaultMood,
  });

  const isV2ListingMode = ['user', 'category', 'tags', 'extreme'].includes(searchState.mode);

  const startPage = useMemo(
    () => Math.max(1, Number(searchState.start) || 1),
    [searchState.start],
  );

  const listingChunkPages = useMemo(() => {
    if (!isV2ListingMode) return Math.max(1, Number(searchState.amount) || 1);
    const limit = searchState.pageLimit;
    if (limit > 0) {
      return Math.min(SEARCH_PAGE_CHUNK, Math.max(1, limit - startPage + 1));
    }
    const fallback = Number(searchState.amount) || SEARCH_PAGE_CHUNK;
    return Math.min(SEARCH_PAGE_CHUNK, Math.max(1, fallback));
  }, [isV2ListingMode, searchState.pageLimit, searchState.amount, startPage]);

  const listingPagesFetchedEndRef = useRef(0);
  /** Listing pages this `run` will fetch (`run` resets `progressCount` to 0). Used while `loading` for X/N bar (can exceed SEARCH_PAGE_CHUNK on share replay). */
  const listingRunProgressTotalRef = useRef(1);

  const searchLogicRunRef = useRef<((offset: number, opts?: RunSearchOptions) => Promise<void>) | null>(null);

  const handleListingBatchComplete = useCallback(
    (info: { offset: number; pageCount: number; append: boolean }) => {
      const endPg = info.offset + info.pageCount - 1;
      if (!info.append) {
        listingPagesFetchedEndRef.current = endPg;
      } else {
        listingPagesFetchedEndRef.current = Math.max(listingPagesFetchedEndRef.current, endPg);
      }
    },
    [],
  );

  const {
    setId,
    setCategories,
    setMoods,
    setErrorMessage,
    setFinished,
    setSearchObject,
    setProgressCount,
  } = searchState;

  const [userIds, setUserIds] = useState<string[]>(() =>
    searchState.params.id ? [String(searchState.params.id)] : [],
  );
  const userIdsRef = useRef(userIds);
  userIdsRef.current = userIds;

  const [userIdChipLabel, setUserIdChipLabel] = useState<Record<string, string>>({});

  const resultsRef = useRef<HTMLDivElement>(null);
  const executeScroll = () => {
    requestAnimationFrame(() => {
      const el = resultsRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      /** Clear sticky TopNav (~14vh) without jumping deep into the results grid. */
      const headerClearance = Math.round(window.innerHeight * 0.12);
      const gap = 8;
      const y = window.scrollY + rect.top - headerClearance - gap;
      window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
    });
  };

  const searchLogic = useSearchLogic({
    mode: searchState.mode,
    type: searchState.type,
    id: searchState.id,
    friendId: searchState.friendId,
    primaryTag: searchState.primaryTag,
    category: searchState.category,
    minDuration: videoFiltering.minDuration,
    scrapeMinDuration: 0,
    logDuration: Math.floor(videoFiltering.minDuration / 60),
    quick: videoFiltering.quick,
    preserveResults: videoFiltering.preserveResults,
    rawVideos: videoFiltering.rawVideos,
    includeTags: videoFiltering.includeTags,
    advanced: true,
    amount: isV2ListingMode ? listingChunkPages : searchState.amount,
    setLoading: searchState.setLoading,
    setProgressCount,
    setRawVideos: videoFiltering.setRawVideos,
    setFinished,
    setErrorMessage,
    setPageLimit: searchState.setPageLimit,
    setAmount: searchState.setAmount,
    setSourceExists: searchState.setSourceExists,
    setUsername: searchState.setUsername,
    setFriends: searchState.setFriends,
    setSearchObject,
    executeScroll,
  });

  searchLogicRunRef.current = searchLogic.run;

  useEffect(() => {
    setId(userIds[0] || '');
  }, [userIds, setId]);

  useEffect(() => {
    const ids = userIds.map((u) => u.trim()).filter(Boolean);
    setUserIdChipLabel((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((k) => {
        if (!ids.includes(k)) delete next[k];
      });
      return next;
    });

    ids.forEach((uid) => {
      getUsername(uid).then((name) => {
        if (!userIdsRef.current.includes(uid)) return;
        const t = name?.trim();
        const label = t && t.length > 0 ? `${t} (${uid})` : uid;
        setUserIdChipLabel((prev) => (prev[uid] === label ? prev : { ...prev, [uid]: label }));
      });
    });
  }, [userIds]);

  const resolveUserChipDisplay = useMemo(() => {
    const labels = userIdChipLabel;
    return (uid: string) => labels[uid] ?? uid;
  }, [userIdChipLabel]);

  useEffect(() => {
    const title = 'ThisVid ASS';
    switch (searchState.mode) {
      case 'user':
        document.title = `${searchState.username || 'User'}'s ${searchState.type} videos — ${title}`;
        break;
      case 'tags':
        document.title = `Tag "${searchState.primaryTag}" — ${title}`;
        break;
      case 'category':
        document.title = `${(searchState.category || 'Category').replace(/-/g, ' ')} — ${title}`;
        break;
      case 'extreme':
        document.title = `Extreme — ${title}`;
        break;
      default:
        document.title = title;
    }
  }, [searchState.mode, searchState.username, searchState.type, searchState.category, searchState.primaryTag]);

  useEffect(() => {
    getCategories().then((categories) => {
      const filtered =
        searchState.categoryType === 'straight'
          ? categories.filter((c) => c.orientation === 'straight')
          : searchState.categoryType === 'gay'
            ? categories.filter((c) => c.orientation === 'gay')
            : categories;
      setCategories(filtered);
    });
  }, [searchState.categoryType, setCategories]);

  useEffect(() => {
    setMoods([SELECT_MOOD_PLACEHOLDER, ...userData.moods]);
  }, [userData.moods, setMoods]);

  useEffect(() => {
    if (searchState.moods.length === 0) return;

    const mood = searchState.moods.find((m) => m.name === videoFiltering.activeMood);

    if (mood?.name === 'Select a mood') {
      videoFiltering.clearMoodPreferences();
      return;
    }

    const preferences = mood?.preferences;

    if (!preferences) {
      return;
    }

    videoFiltering.applyMoodPreferences(preferences);
    // Same pattern as SearchRefactored — applyMoodPreferences identity is not stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoFiltering.activeMood, searchState.moods]);
  useEffect(() => {
    if (searchState.id) {
      searchLogic.updateUsername();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchState.mode, searchState.id]);

  useEffect(() => {
    searchLogic.getPageLimit();
    // Mirror SearchRefactored dependency list — getPageLimitUrl changes with these inputs
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    searchState.sourceExists,
    searchState.type,
    searchState.mode,
    searchState.id,
    searchState.friendId,
    searchState.category,
    searchState.primaryTag,
  ]);

  useEffect(() => {
    const pr = searchState.params;
    if (!pr.run) return;

    let cancelled = false;

    async function replayFromShareOrUrl() {
      const runListing = searchLogicRunRef.current;
      if (!runListing) return;

      const replayStart = Number(pr.start) || LISTING_SHARE_REPLAY_START;
      const totalRaw = pr.totalListingPages;
      const hasTotalListed =
        totalRaw !== undefined &&
        totalRaw !== null &&
        String(totalRaw).trim() !== '' &&
        Number.isFinite(Number(totalRaw));

      const totalListed = hasTotalListed
        ? Math.max(1, Math.floor(Number(totalRaw)))
        : null;

      let listingPagesConsumed = 0;

      try {
        if (totalListed != null) {
          if (!cancelled) {
            listingRunProgressTotalRef.current = Math.max(1, totalListed);
            await runListing(replayStart, {
              append: false,
              pages: totalListed,
              onBatchComplete: handleListingBatchComplete,
            });
          }
          if (!cancelled) {
            listingPagesConsumed = totalListed;
          }
        } else if (!cancelled) {
          const fromUrl = Number(pr.amount);
          const legacyPages =
            Number.isFinite(fromUrl) && fromUrl > 0
              ? Math.floor(fromUrl)
              : Math.min(
                  SEARCH_PAGE_CHUNK,
                  Math.max(1, Number(searchState.amount) || SEARCH_PAGE_CHUNK),
                );
          listingRunProgressTotalRef.current = Math.max(1, legacyPages);
          await runListing(replayStart, {
            pages: legacyPages,
            onBatchComplete: handleListingBatchComplete,
          });
          listingPagesConsumed = legacyPages;
        }

        if (!cancelled && listingPagesConsumed > 0) {
          searchState.setStart(replayStart + listingPagesConsumed);
        }
      } catch {
        // Errors are surfaced via search state inside `run`
      }
    }

    void replayFromShareOrUrl();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleV2Mode = (m: SearchMode) => {
    searchState.setMode(m);
    const opts = types[m];
    if (opts?.length) {
      searchState.setType(opts[0].value);
    }
  };

  const typeOptions = useMemo(() => {
    const m = searchState.mode as keyof Types;
    return types[m] || types.user;
  }, [searchState.mode]);

  const idleSearchPageTotal = Math.max(
    1,
    isV2ListingMode ? listingChunkPages : Number(searchState.amount) || 1,
  );
  const isSearching = searchState.loading;
  const isPreparingPageLimit = isV2ListingMode && searchLogic.pageLimitLoading;
  const isSearchDisabled = isSearching || isPreparingPageLimit;
  const pagesDone = searchState.progressCount;
  const progressBarListingTotal =
    isV2ListingMode && isSearching ? Math.max(1, listingRunProgressTotalRef.current) : idleSearchPageTotal;

  const progressFillPct = isSearching
    ? Math.min(100, (pagesDone / progressBarListingTotal) * 100)
    : 0;

  const hasMoreListingPages =
    isV2ListingMode &&
    Boolean(searchState.finished) &&
    !searchState.loading &&
    searchState.pageLimit > 0 &&
    startPage + listingChunkPages <= searchState.pageLimit;

  const nextBatchPageCount =
    searchState.pageLimit > 0
      ? Math.min(
          SEARCH_PAGE_CHUNK,
          Math.max(1, searchState.pageLimit - (startPage + listingChunkPages) + 1),
        )
      : SEARCH_PAGE_CHUNK;

  const submit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrorMessage('');
    setFinished(false);
    setSearchObject(null);
    if (isV2ListingMode) {
      videoFiltering.setPreserveResults(false);
      listingPagesFetchedEndRef.current = 0;
      searchState.setStart(1);
      const chunk =
        searchState.pageLimit > 0
          ? Math.min(SEARCH_PAGE_CHUNK, searchState.pageLimit)
          : Math.min(SEARCH_PAGE_CHUNK, Math.max(1, Number(searchState.amount) || SEARCH_PAGE_CHUNK));
      listingRunProgressTotalRef.current = Math.max(1, chunk);
      searchLogic.run(LISTING_SHARE_REPLAY_START, {
        append: false,
        pages: chunk,
        onBatchComplete: handleListingBatchComplete,
      });
      return;
    }
    searchLogic.run(Number(searchState.start) || 1);
  };

  const loadNextListingChunk = () => {
    setErrorMessage('');
    const nextStart = startPage + listingChunkPages;
    const chunk = nextBatchPageCount;
    listingRunProgressTotalRef.current = Math.max(1, chunk);
    searchState.setStart(nextStart);
    searchLogic.run(nextStart, {
      append: true,
      pages: chunk,
      onBatchComplete: handleListingBatchComplete,
    });
  };

  const getShareUrl = async () => {
    const cumulativeEndListingPage = listingPagesFetchedEndRef.current;
    const totalListingPagesForShare =
      isV2ListingMode && cumulativeEndListingPage >= LISTING_SHARE_REPLAY_START
        ? cumulativeEndListingPage - LISTING_SHARE_REPLAY_START + 1
        : Math.max(1, isV2ListingMode ? listingChunkPages : Number(searchState.amount) || 1);

    const params: Record<string, string> = {
      mode: searchState.mode,
      activeMood: videoFiltering.activeMood,
      type: searchState.type,
      tags: videoFiltering.includeTags.join(','),
      excludeTags: videoFiltering.excludeTags.join(','),
      boosterTags: videoFiltering.boosterTags.join(','),
      diminishingTags: videoFiltering.diminishingTags.join(','),
      amount: String(isV2ListingMode ? listingChunkPages : searchState.amount),
      minDurationSec: String(videoFiltering.minDuration),
      ...(videoFiltering.maxDuration != null
        ? { maxDurationSec: String(videoFiltering.maxDuration) }
        : {}),
      primaryTag: searchState.primaryTag,
      category: searchState.category,
      id: searchState.id,
      friendId: searchState.friendId,
      termsOperator: videoFiltering.termsOperator,
      orderBy: videoFiltering.sort,
      start: String(LISTING_SHARE_REPLAY_START),
      ...(isV2ListingMode ? { totalListingPages: String(totalListingPagesForShare) } : {}),
      run: 'true',
    };
    const code = await createShortLink('/search', params);
    return `${window.location.origin}/s/${code}`;
  };

  const v2Mode = searchState.mode as SearchMode;
  const isV2Mode = isV2ListingMode;

  return (
    <div className={`v2-root ${chrome.page}`}>
      <TopNav />
      {isV2Mode && <AppSidebar activePage="search" />}

      <main className={chrome.main}>
        <form className={styles.formBlock} onSubmit={submit}>
          {isV2Mode && (
            <div className={styles.tabsBlock}>
              <SearchModeTabs mode={v2Mode} onModeChange={handleV2Mode} />
            </div>
          )}

          {!isV2Mode && (
            <p className={styles.error}>
              This layout is optimized for User / Category / Tags / Extreme. Current mode:{' '}
              <strong>{modes[searchState.mode]}</strong>. Use the{' '}
              <a href="/legacy/search">legacy search</a> page for other modes.
            </p>
          )}

          {searchState.errorMessage && <p className={styles.error}>{searchState.errorMessage}</p>}

          {isV2Mode && (
            <>
              <div className={styles.grid12}>
                <PrimaryParametersCard
                  mode={v2Mode}
                  userIds={userIds}
                  onUserIdsChange={setUserIds}
                  minDuration={videoFiltering.minDuration}
                  maxDuration={videoFiltering.maxDuration}
                  onDurationRangeChange={videoFiltering.setDurationRange}
                  typeOptions={typeOptions}
                  type={searchState.type}
                  onTypeChange={searchState.setType}
                  primaryTag={searchState.primaryTag}
                  onPrimaryTagChange={searchState.setPrimaryTag}
                  categories={searchState.categories}
                  category={searchState.category}
                  categoryType={searchState.categoryType}
                  onPickCategoryType={(v) => searchState.setCategoryType(v)}
                  onCategorySlugChange={searchState.setCategory}
                  resolveUserChipDisplay={resolveUserChipDisplay}
                />
                <MoodTermsCard
                  moods={searchState.moods}
                  activeMood={videoFiltering.activeMood}
                  onActiveMoodChange={(name) => {
                    videoFiltering.setActiveMood(name);
                    void userData.setDefaultMood(name);
                  }}
                  includeTags={videoFiltering.includeTags}
                  onIncludeTagsChange={videoFiltering.setIncludeTags}
                />
              </div>

              <AdvancedScoringSection
                boosterTags={videoFiltering.boosterTags}
                onBoosterChange={videoFiltering.setBoosterTags}
                diminishingTags={videoFiltering.diminishingTags}
                onDiminishingChange={videoFiltering.setDiminishingTags}
                excludeTags={videoFiltering.excludeTags}
                onExcludeChange={videoFiltering.setExcludeTags}
                omitFavourites={videoFiltering.omitFavourites}
                onOmitFavouritesChange={videoFiltering.setOmitFavourites}
                omitPrivate={videoFiltering.omitPrivate}
                onOmitPrivateChange={videoFiltering.setOmitPrivate}
              />

              <div className={styles.actions}>
                <button
                  type="submit"
                  className={styles.searchProgressBtn}
                  disabled={isSearchDisabled}
                  aria-busy={isSearchDisabled}
                >
                  <span className={styles.searchProgressFill} style={{ width: `${progressFillPct}%` }} />
                  <span className={styles.searchProgressLabel}>
                    {isSearching
                      ? `SEARCHING ${pagesDone}/${progressBarListingTotal}...`
                      : isPreparingPageLimit
                        ? 'PREPARING'
                        : `SEARCH ${idleSearchPageTotal} PAGES`}
                  </span>
                </button>
                {hasMoreListingPages ? (
                  <button
                    type="button"
                    className={styles.nextPagesBtn}
                    disabled={isSearchDisabled}
                    onClick={loadNextListingChunk}
                  >
                    NEXT {nextBatchPageCount} PAGES
                  </button>
                ) : null}
              </div>
            </>
          )}
        </form>

        <div className={styles.resultsBlock} ref={resultsRef}>
          {isV2Mode && (
            <ResultsPreviewGrid
              videos={videoFiltering.videos}
              sort={videoFiltering.sort}
              onSortChange={videoFiltering.setSort}
              getShareUrl={getShareUrl}
              searchFinished={searchState.finished}
            />
          )}
          <p className={styles.footerNote}>
            <a href="/legacy/search">Click here for the legacy search</a>
          </p>
        </div>
      </main>
    </div>
  );
};

export default Search;
